import React from "react";
import { arClass } from "../utils/useArClass";
import {
  AlarmClock,
  Check,
  Clock,
  EarthLock,
  Globe,
  KeyRound,
  MapPinCheck,
  MonitorSmartphone,
  TriangleAlert,
  X,
} from "lucide-react";
import { useSelector } from "react-redux";
import formatDate from "../utils/formatDate";
import api from "../api";
import { logout } from "../redux/auth/authActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiRequest, apiSuccess } from "../redux/api/apiActions";
import textTranslator from "../utils/useTextTranslator";
import { clearConfirmationAlerts, hideAlert, showAlert } from "../redux/alert/alertActions";
import { hideEmergencyAlert } from "../redux/emergencyAlert/emergencyAlertActions";
import errorChecker from "../utils/errorChecker";

const colors = {
  low: "bg-green-950/90 border-green-500 text-green-100",
  medium: "bg-yellow-950/90 border-yellow-500 text-yellow-100",
  high: "bg-rose-950/90 border-rose-500 text-rose-100",
};

const getIcon = (type) => {
  const iconClass = "w-5 h-5 flex-shrink-0";
  switch (type) {
    case "low":
      return <Check className={`${iconClass}`} />;
    case "medium":
      return <AlarmClock className={`${iconClass}`} />;
    case "high":
      return <TriangleAlert className={`${iconClass}`} />;
    default:
      return (
        <TriangleAlert
          className={`${iconClass} ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        />
      );
  }
};

const titles = {
  low: {
    en: "Low Priority Alert",
    fr: "Alerte de faible priorité",
    ar: "تنبيه منخفض الأولوية",
  },
  medium: {
    en: "Medium Priority Alert",
    fr: "Alerte de priorité moyenne",
    ar: "تنبيه متوسط الأولوية",
  },
  high: {
    en: "High Priority Alert",
    fr: "Alerte de haute priorité",
    ar: "تنبيه عالي الأولوية",
  },
};

const alertAbouts = {
  "System Maintenance": {
    en: "System Maintenance",
    fr: "Maintenance du système",
    ar: "صيانة النظام",
  },
  "Security Alert": {
    en: "Security Alert",
    fr: "Alerte de sécurité",
    ar: "تنبيه أمني",
  },
  "Data Breach": {
    en: "Data Breach",
    fr: "Violation de données",
    ar: "اختراق البيانات",
  },
};

const SystemMaintenanceAlert = ({ data }) => {
  const defaultLanguage =
    useSelector((state) => state.settings.defaultLanguage) || "en";

  const textTranslations = {
    systemMaintenance: {
      alertAbout: {
        en: "System Maintenance",
        fr: "Maintenance du système",
        ar: "صيانة النظام",
      },
      alertMessage: {
        en: `Our system will undergo maintenance from ${formatDate(data.startTime)} to ${formatDate(data.endTime)}. Please save your work.`,
        fr: `Notre système sera en maintenance de ${formatDate(data.startTime)} à ${formatDate(data.endTime)}. Veuillez sauvegarder votre travail.`,
        ar: `سيخضع نظامنا للصيانة من ${formatDate(data.startTime)} إلى ${formatDate(data.endTime)}. يرجى حفظ عملك.`,
      },
      ScheduledFor: {
        period: {
          en: `Scheduled for: ${formatDate(data.alertTime)}`,
          fr: `Prévu pour : ${formatDate(data.alertTime)}`,
          ar: `مجدول ل: ${formatDate(data.alertTime)}`,
        },
      },
    },
    updateAvailable: {
      alertAbout: {
        en: "Update Available",
        fr: "Mise à jour disponible",
        ar: "تحديث متاح",
      },
      alertMessage: {
        en: `A new update will be available on ${formatDate(data.updateDate)}. Please update to the latest version for the best experience.`,
        fr: `Une nouvelle mise à jour sera disponible le ${formatDate(data.updateDate)}. Veuillez mettre à jour vers la dernière version pour une meilleure expérience.`,
        ar: `سيكون تحديث جديد متاحًا في ${formatDate(data.updateDate)}. يرجى التحديث إلى أحدث إصدار للحصول على أفضل تجربة.`,
      },
    },
    newFeature: {
      alertAbout: {
        en: "New Feature Released",
        fr: "Nouvelle fonctionnalité publiée",
        ar: "ميزة جديدة تم إصدارها",
      },
      alertMessage: {
        en: `We have released a new feature: ${data.featureName}. Check it out in your dashboard!`,
        fr: `Nous avons publié une nouvelle fonctionnalité : ${data.featureName}. Découvrez-la dans votre tableau de bord !`,
        ar: `لقد أصدرنا ميزة جديدة: ${data.featureName}. تحقق منها في لوحة التحكم الخاصة بك!`,
      },
    },
  };
  return (
    <div className={arClass("flex flex-col gap-1 items-start")}>
      <h3 className={arClass("text-base font-semibold")}>
        {textTranslations[data.type].alertAbout[defaultLanguage]}
      </h3>
      <p className={arClass("text-sm")}>
        {textTranslations[data.type].alertMessage[defaultLanguage]}{" "}
      </p>
      <span className={arClass("text-sm")}>
        {textTranslations[data.type].ScheduledFor.period[defaultLanguage]}{" "}
      </span>
    </div>
  );
};

const SecurityAlert = ({ data , activeAlertId }) => {
    const dispatch = useDispatch();
  const defaultLanguage =
    useSelector((state) => state.settings.defaultLanguage) || "en";

  const textTranslations = {
    newLogin: {
      title: {
        en: "Security Alert",
        fr: "Alerte de sécurité",
        ar: "تنبيه أمني",
      },
      recommendation: {
        en: "Recommendation: If this was not you, please contact support immediately and review your account security settings.",
        fr: "Recommandation : Si ce n'était pas vous, veuillez contacter le support immédiatement et revoir les paramètres de sécurité de votre compte.",
        ar: "التوصية: إذا لم تكن أنت، يرجى الاتصال بالدعم فورًا ومراجعة إعدادات أمان حسابك.",
      },
      securityAlertMessage: {
        en: "We have detected unusual login activity. Please review your account security.",
        fr: "Nous avons détecté une activité de connexion inhabituelle. Veuillez vérifier la sécurité de votre compte.",
        ar: "لقد اكتشفنا نشاط تسجيل دخول غير عادي. يرجى مراجعة أمان حسابك.",
      },
      loginData: {
        en: {
          IP: `Login IP: ${data.loginIP}`,
          Time: `Login Time: ${formatDate(data.loginTime, true)}`,
          Method: `Login Method: ${data.loginMethod}`,
          Location: `Login Location: ${data.loginLocation}`,
          Device: `Login Device: ${data.osName}`,
          browserName: `Browser: ${data.browserName}`,
        },
        fr: {
          IP: `IP de connexion : ${data.loginIP}`,
          Time: `Heure de connexion : ${formatDate(data.loginTime, true)}`,
          Method: `Méthode de connexion : ${data.loginMethod}`,
          Location: `Lieu de connexion : ${data.loginLocation}`,
          Device: `Appareil de connexion : ${data.osName}`,
          browserName: `Navigateur : ${data.browserName}`,
        },
        ar: {
          IP: `IP تسجيل الدخول: ${data.loginIP}`,
          Time: `وقت تسجيل الدخول: ${formatDate(data.loginTime, true)}`,
          Method: `طريقة تسجيل الدخول: ${data.loginMethod}`,
          Location: `موقع تسجيل الدخول: ${data.loginLocation}`,
          Device: `جهاز تسجيل الدخول: ${data.osName}`,
          browserName: `المتصفح: ${data.browserName}`,
        },
      },
    },
  };

  const detailsTranslations = {
    en: "Details of the login attempt:",
    fr: "Détails de la tentative de connexion :",
    ar: "تفاصيل محاولة تسجيل الدخول:",
  };

  const handleDelete = () => {
    const deleteKey = `DELETE_SESSION_${data.sessionId}`;
      dispatch(apiRequest("POST", deleteKey));
      api
        .post("auth/logout-all/", { selectedIds: [data.sessionId] })
        .then((res) => {
          dispatch(
            apiSuccess("POST", deleteKey, "User session deleted successfully")
          );
          dispatch(hideEmergencyAlert(activeAlertId));
          dispatch(
            showAlert({
              type: "success",
              title: textTranslator("successTitle", "PROFILE"),
              message: `${data.ipAddress}/${data.osName}/(${data.sessionId})${textTranslator("LoggedOutSuccessfully", "PROFILE")}`,
            })
          );
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.error;
          const type = error.response?.data?.type;
          errorChecker(errorMessage, type, dispatch, errorMessage);
        });
    };
  
  
    
  return (
    <div className={arClass("flex flex-col gap-1 items-start justify-start")}>
      <h3 className={arClass("text-base font-semibold max-sm:text-sm")}>
        {textTranslations[data.type].title[defaultLanguage]}
      </h3>
      <p className={arClass("text-sm max-sm:text-xs")}>
        {textTranslations[data.type].securityAlertMessage[defaultLanguage]}
      </p>
      <p className={arClass("text-sm max-sm:text-xs")}>
        {textTranslations[data.type].recommendation[defaultLanguage]}
      </p>
      <div
        className={arClass(
          "mt-2 p-2 bg-white/20 rounded-lg w-full flex flex-col gap-1 items-start",
        )}
      >
        <h4 className={arClass("text-sm font-semibold mb-1")}>
          {detailsTranslations[defaultLanguage]}
        </h4>
        <ul className={arClass("flex flex-col gap-1 text-sm max-sm:text-xs")}>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <EarthLock className="w-5 h-5" />
            {textTranslations[data.type].loginData[defaultLanguage].IP}
          </li>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <Clock className="w-5 h-5" />
            {textTranslations[data.type].loginData[defaultLanguage].Time}
          </li>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <KeyRound className="w-5 h-5" />
            {textTranslations[data.type].loginData[defaultLanguage].Method}
          </li>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <MapPinCheck className="w-5 h-5" />
            {textTranslations[data.type].loginData[defaultLanguage].Location}
          </li>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <MonitorSmartphone className="w-5 h-5" />
            {textTranslations[data.type].loginData[defaultLanguage].Device}
          </li>
          <li className={arClass("px-1 flex items-center gap-1")}>
            <Globe className="w-5 h-5" />{textTranslations[data.type].loginData[defaultLanguage].browserName} </li>
        </ul>
        <button
          className={arClass(
            `mt-2 px-3 py-1 rounded-lg text-sm font-semibold max-sm:text-xs bg-red-600/80 hover:bg-red-600`,
            )}
            onClick={handleDelete}
        >
          {textTranslator("Logout this new session", "PROFILE")}
        </button>
       
      </div>
    </div>
  );
};


const EmergencyAlert = () => {
  const dispatch = useDispatch();
  const defaultLanguage =
    useSelector((state) => state.settings.defaultLanguage) || "en";
  const emergencyAlerts = useSelector((state) => state.emergencyAlerts || { alerts: [], activeAlert: null });
  const activeAlert = emergencyAlerts.alerts.length > 0 ? emergencyAlerts.alerts[0] : null;

  if (!activeAlert) {
    return null;
  }

  const handleClose = () => {
    dispatch(hideEmergencyAlert(activeAlert.id));
  };

  const { type = 'high', alertType = 'Security Alert', data = {} } = activeAlert;
  const renderAlertContent = () => {
    switch (alertType) {
      case "System Maintenance":
        return (
          <SystemMaintenanceAlert
            data={data.data}
          />
        );
      case "Security Alert":
        return (
          <SecurityAlert
            data={{
              type: "newLogin",
              alertAbout: "Security Alert",
              alertMessage: data.data.alertMessage || "We have detected unusual login activity.",
              recommendation: data.data.recommendation || "Recommendation: If this was not you, please change your password immediately.",
              loginIP: data.data.loginIP || data.data.ip || "Unknown",
              loginTime: data.data.loginTime || new Date(),
              loginMethod: data.data.loginMethod || data.data.method || "Unknown",
              loginLocation: data.data.loginLocation || data.data.location || "Unknown",
              osName: data.data.osName || data.data.device || "Unknown",
              ipAddress: data.data.ipAddress || data.data.ip || "Unknown",
              id: data.data.sessionId,
              browserName: data.data.browserName || "Unknown",
              sessionId: data.data.sessionId,
            }}
            activeAlertId={activeAlert.id}
          />
        );
     
      default:
        return <p>Unknown alert type.</p>;
    }
  };

  return (
    <div
      className={arClass(
        `z-[1500] fixed top-1 left-0 w-full text-white text-center flex justify-center items-center pointer-events-none`,
      )}
    >
      <div
        className={arClass(
          `flex flex-col gap-2 px-4 py-2 rounded-lg text-sm font-semibold max-sm:text-xs w-5/12 items-start max-sm:w-11/12 max-lg:w-9/12 max-xl:w-8/12 ${colors[type]} pointer-events-auto`,
        )}
      >
        <div
          className={arClass("flex items-center justify-between w-full gap-2")}
        >
          <div className={arClass("flex items-center gap-2")}>
            {getIcon(type)}
            <h3 className={arClass(`text-xl font-semibold max-sm:text-lg `)}>
              {titles[type][defaultLanguage]}
            </h3>
          </div>
          <button
            className={arClass(
              `p-1 rounded-full text-white bg-red-400/20 hover:bg-red-400/20 focus:outline-none focus:ring-1 focus:ring-white focus:ring-offset-1`,
            )}
            onClick={handleClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {renderAlertContent()}
      </div>
    </div>
  );
};

export default EmergencyAlert;
